import React, { useEffect, useRef } from 'react';
import './PersistentCesiumBackground.css';

/**
 * Uses the self-hosted window.Cesium build loaded in index.html to avoid bundling the cesium
 * npm package, which uses import.meta and breaks on Vercel/static hosts that load
 * the bundle as a classic script rather than an ES module.
 */
import { getEnvVar } from '../../../utils/env';

/**
 * PersistentCesiumBackground - A Cesium viewer that persists across all routes
 *
 * Creates a single Cesium viewer instance that lives in the background.
 * Camera and layer control are provided via MapContext (useMap).
 *
 * @param {Function} registerViewer - Callback to register the viewer with MapContext
 * @param {Function} unregisterViewer - Callback to unregister on cleanup
 */
const PersistentCesiumBackground = ({ registerViewer, unregisterViewer }) => {
  const cesiumContainerRef = useRef(null);
  const viewerRef = useRef(null);
  const initializingRef = useRef(false);

  useEffect(() => {
    const Cesium = window.Cesium;
    if (!Cesium) {
      console.error('Cesium not loaded. Ensure the CDN script in index.html loads before the app.');
      return;
    }
    if (typeof window !== 'undefined') {
      window.CESIUM_BASE_URL = `${process.env.PUBLIC_URL || ''}/Cesium/`;
      Cesium.buildModuleUrl.setBaseUrl(window.CESIUM_BASE_URL);
    }

    const initializeViewer = async () => {
      if (initializingRef.current || viewerRef.current) {
        return;
      }
      if (!cesiumContainerRef.current) {
        return;
      }

      try {
        initializingRef.current = true;

        const cesiumToken =
          getEnvVar('REACT_APP_CESIUM_ION_TOKEN') ||
          'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI2YjZlNzkwNS0wM2ZlLTQyYWYtODI3NC1kZjMxNDVhZjhiMmUiLCJpZCI6MzIzOTQ2LCJpYXQiOjE3NTMzMDE3MzJ9.HyxZZq_CrVlP0qFp0lw959nj1Yi4mkJ4v9CcNocFYPA';

        if (cesiumToken) {
          Cesium.Ion.defaultAccessToken = cesiumToken;
        }

        const viewer = new Cesium.Viewer(cesiumContainerRef.current, {
          animation: false,
          timeline: false,
          navigationHelpButton: false,
          baseLayerPicker: false,
          fullscreenButton: false,
          vrButton: false,
          homeButton: false,
          sceneModePicker: false,
          geocoder: false,
          infoBox: false,
          selectionIndicator: false,
          shadows: false,
          shouldAnimate: false,
          imageryProvider: false,
        });

        viewerRef.current = viewer;
        // Keep the ellipsoid visibly distinct from space even if every imagery request fails.
        viewer.scene.globe.show = true;
        viewer.scene.globe.baseColor = new Cesium.Color(0.45, 0.55, 0.65, 1.0);

        try {
          const fallbackProvider = await Cesium.TileMapServiceImageryProvider.fromUrl(
            Cesium.buildModuleUrl('Assets/Textures/NaturalEarthII')
          );
          viewer.imageryLayers.addImageryProvider(fallbackProvider);
        } catch (fallbackImageryError) {
          console.warn('Could not load Natural Earth fallback basemap:', fallbackImageryError);
        }

        try {
          const oceansProvider =
            await Cesium.ArcGisMapServerImageryProvider.fromBasemapType(
              Cesium.ArcGisBaseMapType.OCEANS
            );
          viewer.imageryLayers.addImageryProvider(oceansProvider);
        } catch (imageryError) {
          console.warn('Could not load Oceans basemap:', imageryError);
        }

        try {
          const dimStage = new Cesium.PostProcessStage({
            fragmentShader: `
              uniform sampler2D colorTexture;
              in vec2 v_textureCoordinates;
              const float BRIGHTNESS = 0.75;
              const float CONTRAST   = 1.6;
              const float SATURATION = 1.0;
              const float GAMMA      = 0.50;
              vec3 adjustBrightness(vec3 color, float value) { return color * value; }
              vec3 adjustContrast(vec3 color, float value) { return ((color - 0.5) * value) + 0.5; }
              vec3 adjustSaturation(vec3 color, float value) {
                float gray = dot(color, vec3(0.299, 0.587, 0.114));
                return mix(vec3(gray), color, value);
              }
              vec3 adjustGamma(vec3 color, float gamma) { return pow(color, vec3(1.0 / gamma)); }
              void main(void) {
                vec4 tex = texture(colorTexture, v_textureCoordinates);
                vec3 color = tex.rgb;
                color = adjustBrightness(color, BRIGHTNESS);
                color = adjustContrast(color, CONTRAST);
                color = adjustSaturation(color, SATURATION);
                // Avoid undefined pow() results when contrast produces negative channels.
                color = clamp(color, 0.0, 1.0);
                color = adjustGamma(color, GAMMA);
                out_FragColor = vec4(color, tex.a);
              }
            `,
          });
          viewer.scene.postProcessStages.add(dimStage);
        } catch (postProcessError) {
          console.warn('Could not add Cesium post-process dimming stage:', postProcessError);
        }

        if (viewer.cesiumWidget?.creditContainer) {
          viewer.cesiumWidget.creditContainer.style.display = 'none';
        }

        // Enable interactions by default - pages using the map expect zoom/pan/drag
        viewer.scene.screenSpaceCameraController.enableRotate = true;
        viewer.scene.screenSpaceCameraController.enableTranslate = true;
        viewer.scene.screenSpaceCameraController.enableZoom = true;
        viewer.scene.screenSpaceCameraController.enableTilt = true;
        viewer.scene.screenSpaceCameraController.enableLook = true;

        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(-40.0, 35.0, 20000000),
          orientation: {
            heading: 0.0,
            pitch: -Cesium.Math.PI_OVER_TWO,
            roll: 0.0,
          },
        });

        try {
          const terrainProvider =
            await Cesium.CesiumTerrainProvider.fromIonAssetId(2426648, {
              requestVertexNormals: true,
            });
          viewer.scene.setTerrain(new Cesium.Terrain(terrainProvider));
        } catch (terrainError) {
          console.warn(
            'Could not load configured terrain; trying Cesium World Bathymetry:',
            terrainError
          );
          // Cesium.Terrain.fromWorldBathymetry() returns synchronously and only reports
          // failure via errorEvent later (not a rejected promise/thrown error), so a
          // try/catch around it never sees a bad ion asset - listen for errorEvent instead
          // and fall back to the always-ready ellipsoid terrain so the globe doesn't get
          // stuck with an unresolved terrainProvider (which renders as an invisible globe).
          const bathymetryTerrain = Cesium.Terrain.fromWorldBathymetry({
            requestVertexNormals: true,
          });
          bathymetryTerrain.errorEvent.addEventListener((bathymetryError) => {
            console.warn(
              'Could not load Cesium World Bathymetry; using flat terrain:',
              bathymetryError
            );
            viewer.scene.setTerrain(
              new Cesium.Terrain(new Cesium.EllipsoidTerrainProvider())
            );
          });
          viewer.scene.setTerrain(bathymetryTerrain);
        }

        registerViewer(viewer);
      } catch (error) {
        console.error('Failed to initialize Cesium viewer:', error);
        initializingRef.current = false;
      }
    };

    initializeViewer();

    return () => {
      if (viewerRef.current) {
        unregisterViewer();
        if (!viewerRef.current.isDestroyed()) {
          viewerRef.current.destroy();
        }
        viewerRef.current = null;
        initializingRef.current = false;
      }
    };
  }, [registerViewer, unregisterViewer]);

  return (
    <div ref={cesiumContainerRef} className="persistent-cesium-background" />
  );
};

export default PersistentCesiumBackground;
